import Colors from '@utils/Colors.js'

// All form components take their look from here: sizes, spacing and colours of the field box, its floating label
// and its state colours (focused, invalid, disabled, readOnly). Change a value here instead of in a component,
// so every field stays alike.

export const field = {
    height: 42,
    paddingX: 10,
    // Room above the text for the floated label
    paddingTop: 12,
    paddingBottom: 2,
    // TextArea grows with its content up to this many lines of textAreaLineHeight, then it scrolls
    textAreaMaxRows: 10,
    // Editor (as a textarea) starts at 10 lines and grows up to this many
    editorMaxRows: 20,
    textAreaLineHeight: 16,
    borderRadius: '4px 4px 0px 0px',
    fontSize: 14,
    // Text inset next to a prepend/append
    prependWidth: 24,
    appendWidth: 24,
    // Fields have no outer margins: spacing in a form comes from FormStack/FormRow (formSpacing)
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0
}

// All spacing in forms, change it here:
// - rowGap: vertical space between fields (FormStack), between wrapped lines, and after a block of fields
// - columnGap: horizontal space between fields (FormRow) and between buttons in a row
// - inset: padding of a card that holds fields, so fields sit as far from a card edge as from each other
// - tight: a field and the icon button that belongs to it (edit, delete)
// - section: extra space around larger blocks (groups of rows, sections before a heading)
export const formSpacing = {
    rowGap: 12,
    columnGap: 12,
    inset: 12,
    tight: 6,
    section: 24
}

// The outer margins of a field, from the tokens above. Components spread this into their outer style (before the
// caller's style) instead of writing the numbers themselves.
export const fieldMargins = {
    marginLeft: field.marginLeft,
    marginRight: field.marginRight,
    marginBottom: field.marginBottom
}

// Components whose outer margins differ from fieldMargins today. Change them here, not in the component.
export const componentMargins = {
    slider: fieldMargins,
    fileUpload: fieldMargins
}

// The drop-down caret at the right of a field that opens a list or popover (Select, IconPicker), see FieldCaret
export const caret = {
    icon: 'mdi mdi-menu-down',
    top: 10,
    right: 10
}

export const label = {
    fontSize: 14,
    fontSizeFloated: 12,
    top: 10,
    topFloated: 2,
    paddingLeft: 10,
    // Resting label next to a prepend
    paddingLeftPrepend: 36,
    transition: 'all .2s ease'
}

export const invalidIcon = {
    // Distance from the top of the field, centres the icon on the (first) line of text
    top: 12,
    right: 10,
    size: 20,
    // Space between the end of the text and the icon's box (the glyph reaches about 3px past the box's left edge)
    gap: 8
}

export const formColors = {
    text: Colors.textDark,
    textDisabled: Colors.textMedium,
    label: Colors.textMedium,
    placeholder: Colors.textMedium,
    border: Colors.border,
    borderFocus: Colors.buttonSolid,
    borderInvalid: Colors.errorBright,
    background: Colors.backgroundWhite,
    backgroundDisabled: Colors.grey20,
    backgroundReadOnly: 'transparent'
}

interface FieldState {
    disabled?: boolean
    readOnly?: boolean
    invalid?: boolean
    focused?: boolean
}

export function getFieldBorderColor({disabled, readOnly, invalid, focused}: FieldState) {
    if (disabled && !readOnly) return formColors.border
    if (invalid) return formColors.borderInvalid
    if (focused) return formColors.borderFocus
    return formColors.border
}

export function getFieldBackground({disabled, readOnly}: FieldState) {
    if (readOnly) return formColors.backgroundReadOnly
    if (disabled) return formColors.backgroundDisabled
    return formColors.background
}

export function getFieldTextColor({disabled, readOnly}: FieldState) {
    return disabled && !readOnly ? formColors.textDisabled : formColors.text
}

export function getFieldCursor({disabled, readOnly}: FieldState) {
    return disabled || readOnly ? 'default' : 'text'
}

interface TrailingPaddingOptions {
    /** Invalid: the text also has to stop before the invalid icon */
    invalid?: boolean | string
    /** The trailingWidth passed to FieldBox: content at the right (append icon, caret, buttons) left of which the icon sits */
    trailingWidth?: number
    /** The control ends at a text append: FieldBox then places the icon at the append's left edge instead of invalidIcon.right */
    textAppend?: boolean
    /** Right padding of the control when valid */
    padding?: number
}

/**
 * Right padding of a field's control (<input>, <textarea>, Select's value), measured from the control's right edge, so
 * its text stops before the content at the right and, when invalid, before the invalid icon (see InvalidIcon)
 */
export function getTrailingPadding({invalid, trailingWidth = 0, textAppend, padding = field.paddingX}: TrailingPaddingOptions) {
    if (!invalid) {
        return padding
    }

    const iconRight = trailingWidth + (textAppend ? 0 : invalidIcon.right)

    return Math.max(padding, iconRight + invalidIcon.size + invalidIcon.gap)
}
